import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTraceableComment } from 'services/traceable-comment';
import { CustomNotification } from 'common/components';
import { updateTraceableCommentCount } from 'services/traceable-comment';
import { useCheckAllowViewQaCommentWorkflow } from 'pages/qa-spec/hooks';
import { useCallback, useRef } from 'react';
import { useCheckIsSuperAdmin, useCheckMemberSupplier } from 'hooks';

export const useTraceableComments = ({ productId, isTabActive }) => {
  return useQuery({
    queryKey: ['traceable-comments', productId],
    queryFn: async () => {
      const response = await getTraceableComment({ productId });

      if (!response || !Array.isArray(response)) {
        throw new Error('Invalid response format');
      }

      return response.map((item, index) => ({
        key: index,
        qaSpec: item.qaSpec || '',
        fieldName: item.fieldName || '',
        supplierComment: item.supplierComment || '',
        updatedBySupplier: item.updatedBySupplier || '',
        lastUpdatedSupplier: item.lastUpdatedSupplier || '',
        qaComments: item.qaComments || '',
        updatedByQA: item.updatedByQA || '',
        lastUpdatedQA: item.lastUpdatedQA || '',
        visibility: false,
      }));
    },
    enabled: !!productId && isTabActive,
    onError: () => {
      CustomNotification.error('Failed to fetch traceable comments');
    },
  });
};

export const useUpdateCommentCountOnTabClick = ({ productId, ownerId }) => {
  const queryClient = useQueryClient();
  const isQAUser = useCheckAllowViewQaCommentWorkflow();
  const isSupplierUser = useCheckMemberSupplier();
  const isSuperAdmin = useCheckIsSuperAdmin();

  const hasCalled = useRef(false);

  const handleCommentsActionClick = useCallback(() => {
    if (!productId || !ownerId || hasCalled.current) return;

    const isAllowed = (isQAUser || isSupplierUser) && !isSuperAdmin;

    if (isAllowed) {
      updateTraceableCommentCount({
        ProductId: productId,
        OwnerId: ownerId,
        IsSupplier: isSupplierUser,
      }).then(() => {
        queryClient.invalidateQueries([
          'traceable-comment-count',
          productId,
          ownerId,
        ]);
      });

      hasCalled.current = true;
    }
  }, [productId, ownerId, isQAUser, isSupplierUser, isSuperAdmin, queryClient]);

  return { handleCommentsActionClick };
};
